import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { WebConClient, ClientContract } from '../types';

export function useClients(filters: { page?: number; pageSize?: number; search?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.search) params.set('search', filters.search);
  const q = params.toString();

  return useQuery({
    queryKey: ['clients', filters],
    queryFn: () => api.get<WebConClient[]>(`/clients${q ? `?${q}` : ''}`),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });
}

export function useClient(id: string | number) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get<WebConClient>(`/clients/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useClientContracts(id: string | number) {
  return useQuery({
    queryKey: ['clients', id, 'contracts'],
    queryFn: () => api.get<ClientContract[]>(`/clients/${id}/contracts`),
    enabled: !!id,
  });
}

export function useClientContacts(id: string | number) {
  return useQuery({
    queryKey: ['clients', id, 'contacts'],
    queryFn: () => api.get<unknown[]>(`/clients/${id}/contacts`),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 min — contacts should be relatively fresh
    retry: 1,
  });
}

export function useClientNotificationRecipients(id: string | number) {
  return useQuery({
    queryKey: ['clients', id, 'notification-recipients'],
    queryFn: () => api.get<string[]>(`/clients/${id}/notification-recipients`),
    enabled: !!id,
  });
}

export function useUpdateClientNotificationRecipients(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      api.put<string[]>(`/clients/${id}/notification-recipients`, { userIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients', id, 'notification-recipients'] });
    },
  });
}
